import axios, { AxiosRequestConfig, isAxiosError, ResponseType } from "axios";
import { AppError, ERR_CODE } from "../error";
import * as util from "../../util";

export function attachLastDash(url: string) {
  if (!url || url.length === 0) {
    return "";
  }
  // eslint-disable-next-line no-magic-numbers
  if (url.substr(-1) !== "/") {
    return `${url}/`;
  }
  return url;
}

interface CallConfig {
  method: "post" | "get" | "delete";
  url: string;
  data: unknown;
  responseType?: ResponseType;
}

export default class ServiceCall {
  tokenPromise: Promise<string> | null;

  constructor(tokenPromiseOrNull: Promise<string> | null) {
    this.tokenPromise = tokenPromiseOrNull;
  }

  Post<T>(absoluteUrl: string, data: unknown) {
    return this.Request<T>({ method: "post", url: absoluteUrl, data });
  }

  Delete<T>(absoluteUrl: string, data: unknown) {
    return this.Request<T>({ method: "delete", url: absoluteUrl, data });
  }

  Get<T>(absoluteUrl: string, data: unknown) {
    return this.Request<T>({ method: "get", url: absoluteUrl, data });
  }

  Download(absoluteUrl: string, post: boolean, data: unknown): Promise<Blob> {
    return this.Request<Blob>({
      method: post ? "post" : "get",
      url: absoluteUrl,
      data,
      responseType: "blob"
    });
  }

  async Request<T>(config: CallConfig) {
    try {
      let result: T;
      if (this.tokenPromise) {
        // Use firebase user token in the call
        const token = await this.tokenPromise;
        if (token) {
          const newConfig = {
            ...config,
            headers: { Authorization: `Bearer ${token}` }
          };
          result = await this.runToService(newConfig);
        } else {
          result = await this.runToService(config);
        }
      } else {
        // Call without token header
        result = await this.runToService(config);
      }
      util.log(`HTTP ${config.method} ${config.url} SUCCESS!`, {
        requestBody: config.data,
        result: result instanceof Blob ? `Blob(${result.size} bytes)` : result
      });
      return result;
    } catch (err) {
      util.logError(`HTTP ${config.method} ${config.url} ERROR: ${(err as Error).message}`, {
        requestBody: config.data,
        error: err
      });
      throw err;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async runToService<T>(config: Partial<AxiosRequestConfig>): Promise<T> {
    try {
      const httpResponse = await axios.request<RemoteErrorResponse<T>>({
        responseEncoding: "utf8",
        ...config
      });
      const body = httpResponse.data ?? null;
      if (body instanceof Blob) {
        // This is a hack to be able to return Blob immediately
        return body as unknown as T;
      }
      if (!body) {
        throw new AppError(
          ERR_CODE.ServiceBadResponseFormat,
          "HTTP response has no data",
          "Remote error, no response received"
        );
      }
      if (body.error) {
        throw this.appErrorFromErrorEnv(body.error);
      }
      if (!body.data) {
        throw new AppError(
          ERR_CODE.ServiceBadResponseFormat,
          "Response envelope is empty",
          "Remote error, invalid response, missing content"
        );
      }
      return body.data;
    } catch (err) {
      throw this.wrapError(err as Error);
    }
  }

  wrapError(rperr: Error): AppError {
    if (!isAxiosError(rperr)) {
      return new AppError(ERR_CODE.ServiceUnhandled, rperr.message, "", rperr);
    }
    if (rperr.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (rperr.response.data && rperr.response.data.error) {
        // There is a proper error object in data
        return this.appErrorFromErrorEnv(rperr.response.data.error, rperr);
      }
      // No error object, probably an unhandled error in the server
      return new AppError(ERR_CODE.ServiceRemoteError, rperr.message, "", rperr);
    }
    if (rperr.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      return new AppError(ERR_CODE.ServiceNetwork, rperr.message, "", rperr);
    }
    // Something happened in setting up the request that triggered an Error
    return new AppError(ERR_CODE.ServiceBadConfig, rperr.message, "", rperr);
  }

  appErrorFromErrorEnv(err: RemoteError, cause?: Error): AppError {
    // In the server this is de ErrorEnv type
    return new AppError(err.code, err.message, err.userMessage, cause);
  }
}

interface RemoteErrorResponse<T> {
  error?: RemoteError;
  data?: T;
}

interface RemoteError {
  code: string;
  message: string;
  userMessage?: string;
}
